import React, { ComponentProps } from 'react';

import PropTypes from 'prop-types';

import { IconButton } from '@adsk/alloy-react-button';
import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { XIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { stylePropType } from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

const defaultRenderTarget = (props: ComponentProps<typeof Tooltip.Target>) => (
  <Tooltip.Target {...props} shouldWrapChildren={false} />
);

/**
 * Close Button.
 */
const CloseButton = React.forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof IconButton> & {
    showEscapeLabel?: boolean;
    tooltipProps?: Omit<ComponentProps<typeof Tooltip>, 'content'>;
  }
>(
  (
    {
      style,
      children,
      disabled = false,
      showEscapeLabel = true,
      type = 'button',
      renderIcon = XIcon.renderIcon,
      tooltipProps,
      ...props
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const target = useForwardedRef(ref);

    return (
      <Tooltip
        data-testid="close-button-tooltip"
        content={
          showEscapeLabel ? T.translate('CLOSE_ESC') : T.translate('CLOSE')
        }
        target={target}
        getTargetProps={() => ({
          ['data-testid']: 'close-button',
          disabled,
          type,
          renderIcon,

          ...props,
        })}
        renderTarget={defaultRenderTarget}
        {...tooltipProps}
      >
        <IconButton
          style={[
            {
              height: 24,
            },
            style,
          ]}
        />
      </Tooltip>
    );
  },
);

CloseButton.displayName = 'CloseButton';

CloseButton.propTypes = {
  /** BasicButton's content */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** button html type, eg: submit */
  type: IconButton.propTypes?.type,
  /** Disable button functionality */
  disabled: PropTypes.bool,
  /** handle onclick event for button */
  onClick: PropTypes.func,
  /** Modify tooltip if Esc will be used to close */
  showEscapeLabel: PropTypes.bool,
};

export default CloseButton;
